/**
 * Use this as the generic file upload by using the Uppy tool
 * Because this uppy is pure ESM only it cannot be used in the backend for now, which is being build using webpack
 * We need to make a modern backend build that can support both ESM and CJS output
 */

import type { File } from '@ztechapps/common.contract'
import React, { useEffect, useState } from 'react'
import Uppy, { type Meta } from '@uppy/core'
import type { AwsBody } from '@uppy/aws-s3'
import AwsS3 from '@uppy/aws-s3'
import { useCreateManyFile } from '@ztechapps/common.hook'
import Webcam from '@uppy/webcam'
import Compressor from '@uppy/compressor'
import ScreenCapture from '@uppy/screen-capture'
import { formatDate, generateId, sanitizeUri } from '@ztechapps/common.utils'
import isEmpty from 'lodash/isEmpty'
import type { OnSetFileMetaFunction, OnUploadCompleteFunction } from './index'

export type UseUppyFileUploadResult = {
  uppy: Uppy<Meta, AwsBody>
  uploadedFiles: File[]
}

/**
 * Uppy restrictions configuration for file uploads.
 * @see https://uppy.io/docs/uppy/#restrictions
 */
export type UppyRestrictions = {
  /** Maximum file size in bytes (e.g., 10 * 1024 * 1024 for 10MB) */
  maxFileSize?: number
  /** Minimum file size in bytes */
  minFileSize?: number
  /** Maximum total size of all files in bytes */
  maxTotalFileSize?: number
  /** Maximum number of files that can be uploaded */
  maxNumberOfFiles?: number
  /** Minimum number of files required */
  minNumberOfFiles?: number
  /**
   * Allowed file types. Can be MIME types or file extensions.
   * Examples: ['.pdf', 'application/pdf', 'image/*', '.jpg', '.png']
   */
  allowedFileTypes?: string[] | null
  /** Custom error message for file type restrictions */
  requiredMetaFields?: string[]
}

export type UseUppyFileUploadProps = {
  onSetFileMeta?: OnSetFileMetaFunction
  onUploadComplete?: OnUploadCompleteFunction
  /** Optional restrictions for file uploads (size, type, count) */
  restrictions?: UppyRestrictions
}

export const useUppyFileUpload = (
  { onSetFileMeta, onUploadComplete, restrictions } = {} as UseUppyFileUploadProps
): UseUppyFileUploadResult => {
  const [uppy] = useState(() =>
    new Uppy<Meta, AwsBody>({
      restrictions,
    })
      .use(Webcam, {
        mirror: false,
        videoConstraints: {
          facingMode: 'environment',
        },
      })
      .use(Compressor)
      .use(ScreenCapture)
      .use(AwsS3, {
        endpoint: '/api/file/companion',
        shouldUseMultipart: true,
      })
  )
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([])
  const [createFile] = useCreateManyFile()

  useEffect(() => {
    const _onFileAdded = (file) => {
      const customMeta = onSetFileMeta?.(file) ?? {}
      const fileMeta = {
        folder: formatDate(new Date(), 'YYYY-MM-DDTHH-mm-ss-sss'),
        ...customMeta,
      }

      uppy.setFileMeta(file.id, fileMeta)
    }
    uppy.on('file-added', _onFileAdded)

    const onComplete = (result) => {
      const successfulFiles = result.successful.map((item) => {
        const data = item?.data as globalThis.File
        const key = item?.response?.body?.key
        return {
          _id: generateId(),
          uri: sanitizeUri(`https://${item.uploadURL}`),
          name: item.name,
          mimetype: item.type,
          bucket: item?.response?.body?.bucket,
          key,
          size: item?.size,
          updatedAt: new Date(data.lastModified),
          directoryTree: key.split('/'),
          tags: item?.meta?.tags,
          description: item?.meta?.description,
        } as File
      })

      setUploadedFiles(successfulFiles)

      //if there is value for successFiles, then we call the createFile mutation
      if (!isEmpty(successfulFiles)) {
        //fire and forget
        createFile({ input: successfulFiles })
      }

      onUploadComplete?.(successfulFiles)
    }
    uppy.on('complete', onComplete)

    return () => {
      uppy.off('complete', onComplete)
      uppy.off('file-added', _onFileAdded)
      //uppy.destroy()
    }
    // `createFile` is deliberately excluded — `useCreateManyFile`/`useMutation`'s `executeIt` is a
    // plain function recreated every render (not memoized), so depending on it would tear down and
    // re-subscribe these uppy listeners on every render. The effect only needs a working
    // `createFile` at call time, which the closure already provides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy, onSetFileMeta, onUploadComplete])

  return {
    uppy,
    uploadedFiles,
  }
}
